import {
  computed,
  nextTick,
  onUnmounted,
  ref,
  shallowRef,
  triggerRef,
  watch,
  type ComputedRef,
  type Ref,
} from 'vue';
import type { VirtualWindow } from './types';
import { buildOffsets, computeWindow, parseRowHeightPx } from './virtualHeights';

const VIRTUAL_OVERSCAN = 10;
const NO_LEADING: readonly string[] = Object.freeze([]);

export function useVirtualScroll<T>(params: {
  /* ref to the element containing the children (where you want to scroll in) */
  tableRef: Ref<HTMLElement | undefined>;
  /** Bottom overlay that covers body rows without belonging to the virtual list. */
  bottomInset?: Readonly<Ref<number>>;
  data: ComputedRef<T[]>;
  /* stable per-row identity — measured heights are cached by this key */
  rowIdKey: (row: T) => string;
  /* how many rows to render before we have a measurement to size the window */
  initialRenderLimit: ComputedRef<number>;
  /* estimate seed; also still the row's CSS min-height in the template */
  bodyRowHeight: ComputedRef<string>;
  /** Whether body rows grow with content or have one fixed virtual height. */
  rowSizing: ComputedRef<'auto' | 'uniform'>;
  /* used to never un-render the dragged row */
  draggedRow: Ref<number | null>;
  /**
   * While true, row-height re-measurements are BUFFERED instead of applied
   * (flushed when it flips back). Used during column-resize drags: every
   * mousemove changes row heights, and applying each one re-renders the whole
   * table — the feedback loop that made resizing feel like slow motion.
   */
  suspendMeasure?: ComputedRef<boolean>;
  /** Windowed shells — never contribute their short shimmer height to the estimate. */
  isPlaceholderRow?: (row: T) => boolean;
  /**
   * rAF-coalesced rendered row window. `endIndex` is exclusive so callers can
   * forward it directly to a range loader.
   */
  onVisibleRangeChange?: (range: { startIndex: number; endIndex: number }) => void;
  /**
   * Keys of the extra rows (group headers) rendered directly ABOVE row `index`.
   * They belong to that row's virtual slot — rendered exactly when the row is —
   * so offsets, the window and scroll anchoring include their height, measured
   * per key through `measureLeading`.
   */
  leadingKeysOf?: (index: number) => readonly string[];
  /** Whether any row has leading rows; uniform rows then leave their O(1) offset math. */
  hasLeadingRows?: ComputedRef<boolean>;
  /**
   * Keys of the extra rows rendered directly BELOW the row with `rowId` (an
   * open row's detail). Like leading rows they belong to the row's slot and are
   * measured per key through `measureLeading`; the row itself stays at the
   * slot's top. Keyed by row id, not index: when the rows change, the current
   * answer still describes the old layout (the owner drops rows that left
   * `data` only after this list has re-anchored).
   */
  trailingKeysOf?: (rowId: string) => readonly string[];
  /** Whether any row has trailing rows; uniform rows then leave their O(1) offset math. */
  hasTrailingRows?: ComputedRef<boolean>;
}) {
  const scrollTop = ref(0);
  const containerHeight = ref(0);

  // Measured row heights, keyed by stable rowId. Held in a shallowRef so the
  // plain Map can be mutated in place (no per-key proxy overhead); a manual
  // triggerRef forces offset/estimate recomputation after a mutation.
  const measured = shallowRef(new Map<string, number>());
  const observed = new Map<string, HTMLElement>();
  /** Reverse lookup for the ResizeObserver callback — the old linear scan over
   *  `observed` per entry was O(rows²) per batch. */
  const rowIdByEl = new WeakMap<HTMLElement, string>();
  let ro: ResizeObserver | undefined;

  // Font size an `em` in `bodyRowHeight` resolves against: the table's, read
  // once the scroller exists (rows inherit it). Without it `calc(3em / 0.875)`
  // was taken at 16px — 54.86px instead of 48px at the 14px table step.
  const emPx = shallowRef<number | null>(null);
  watch(
    params.tableRef,
    (el) => {
      if (typeof Element === 'undefined' || !(el instanceof Element)) return;
      const px = parseFloat(getComputedStyle(el).fontSize);
      if (Number.isFinite(px) && px > 0) emPx.value = px;
    },
    { immediate: true }
  );
  const seedPx = computed(() =>
    parseRowHeightPx(params.bodyRowHeight.value, undefined, emPx.value ?? undefined)
  );
  const isUniform = computed(() => params.rowSizing.value === 'uniform');
  // Uniform rows: the exact pixel height comes from ONE reference row (the
  // first rendered), not from parsing the CSS string — so `bodyRowHeight` may
  // be em/token-based and follow the table font + scale. Until a row exists the
  // parsed seed carries the estimate.
  const uniformPx = shallowRef<number | null>(null);
  let uniformEl: HTMLElement | null = null;
  /** Row id of `uniformEl`: only ITS unmount drops the reference. */
  let uniformRowId: string | null = null;
  let uniformRo: ResizeObserver | undefined;
  const uniformRowPx = computed(() => uniformPx.value ?? seedPx.value);

  // Leading rows (group headers), measured per key like body rows per id. An
  // unmeasured one is taken as tall as the average measured one, before any
  // measurement as tall as a body row.
  const measuredLeading = shallowRef(new Map<string, number>());
  const leadingObserved = new Map<string, HTMLElement>();
  const leadingKeyByEl = new WeakMap<HTMLElement, string>();
  let leadingRo: ResizeObserver | undefined;
  const hasLeading = computed(
    () => !!params.leadingKeysOf && params.hasLeadingRows?.value === true
  );
  const hasTrailing = computed(
    () => !!params.trailingKeysOf && params.hasTrailingRows?.value === true
  );
  // Uniform rows keep `offset = index × height` only while nothing sits between them.
  const uniformFast = computed(() => isUniform.value && !hasLeading.value && !hasTrailing.value);

  const leadingEstimate = computed(() => {
    const m = measuredLeading.value;
    if (m.size === 0) return seedPx.value;
    let sum = 0;
    for (const h of m.values()) sum += h;
    return sum / m.size;
  });

  function leadingHeightOfKeys(keys: readonly string[]): number {
    if (keys.length === 0) return 0;
    const m = measuredLeading.value;
    let sum = 0;
    for (const key of keys) sum += m.get(key) ?? leadingEstimate.value;
    return sum;
  }

  function leadingKeysAt(index: number): readonly string[] {
    return hasLeading.value ? params.leadingKeysOf!(index) : NO_LEADING;
  }

  function leadingHeightOf(index: number): number {
    return leadingHeightOfKeys(leadingKeysAt(index));
  }

  function trailingHeightOfId(rowId: string): number {
    return hasTrailing.value ? leadingHeightOfKeys(params.trailingKeysOf!(rowId)) : 0;
  }

  function trailingHeightOf(index: number): number {
    if (!hasTrailing.value) return 0;
    const row = params.data.value[index];
    return row ? trailingHeightOfId(params.rowIdKey(row)) : 0;
  }

  const estimate = computed(() => {
    if (isUniform.value) return uniformRowPx.value;
    const m = measured.value;
    const rows = params.data.value;
    let sum = 0;
    let n = 0;
    for (const row of rows) {
      if (params.isPlaceholderRow?.(row)) continue;
      const h = m.get(params.rowIdKey(row));
      if (h === undefined) continue;
      sum += h;
      n += 1;
    }
    // Never return 0 — a zero estimate collapses bottomPad and the scrollbar
    // only covers the first loaded page (scroll-to-end lands at ~row 100/150).
    return n > 0 ? sum / n : seedPx.value;
  });

  const hasUnmeasured = computed(() => {
    if (isUniform.value) return false;
    const rows = params.data.value;
    const m = measured.value;
    for (const row of rows) {
      if (!row || params.isPlaceholderRow?.(row)) return true;
      if (!m.has(params.rowIdKey(row))) return true;
    }
    return false;
  });

  function rowHeightOf(i: number): number {
    if (isUniform.value) return uniformRowPx.value;
    const row = params.data.value[i];
    if (!row || params.isPlaceholderRow?.(row)) return Math.ceil(estimate.value);
    return measured.value.get(params.rowIdKey(row)) ?? Math.ceil(estimate.value);
  }

  /** The row's virtual slot: its leading rows (group headers), the row, its trailing rows (detail). */
  function heightOf(i: number): number {
    return rowHeightOf(i) + leadingHeightOf(i) + trailingHeightOf(i);
  }

  const offsets = computed<number[]>(() => {
    void measured.value; // tracked: heightOf reads the Map inside the closure
    void measuredLeading.value;
    return buildOffsets(params.data.value.length, heightOf);
  });

  function offsetOf(index: number): number {
    if (uniformFast.value) {
      const clamped = Math.max(0, Math.min(index, params.data.value.length));
      return clamped * uniformRowPx.value;
    }
    const o = offsets.value;
    const clamped = Math.max(0, Math.min(index, o.length - 1));
    const base = o[clamped] ?? 0;
    // One extra estimated row when the list is still sparse — accumulated
    // rounding otherwise stops the thumb one slot short (157 of 158).
    if (index >= params.data.value.length && hasUnmeasured.value) {
      return base + estimate.value;
    }
    return base;
  }
  const totalHeight = computed(() => offsetOf(params.data.value.length));

  const virtualWindow = computed<VirtualWindow>(() => {
    const total = params.data.value.length;
    // Bootstrap only until the scroller has a box. Estimate is seeded from
    // bodyRowHeight so we can already pad to `count * row` (full scrollbar).
    if (containerHeight.value === 0) {
      return {
        start: 0,
        end: Math.min(total, params.initialRenderLimit.value),
        topPad: 0,
        bottomPad: 0,
      };
    }
    if (uniformFast.value) {
      if (total === 0) return { start: 0, end: 0, topPad: 0, bottomPad: 0 };

      const rowHeight = uniformRowPx.value;
      const firstVisible = Math.max(
        0,
        Math.min(total - 1, Math.floor(scrollTop.value / rowHeight))
      );
      const lastVisible = Math.max(
        0,
        Math.min(total - 1, Math.floor((scrollTop.value + containerHeight.value) / rowHeight))
      );
      let start = Math.max(0, firstVisible - VIRTUAL_OVERSCAN);
      let end = Math.min(total, lastVisible + 1 + VIRTUAL_OVERSCAN);
      const dragged = params.draggedRow.value;
      if (dragged !== null && dragged >= 0 && dragged < total) {
        if (dragged < start) start = dragged;
        if (dragged >= end) end = dragged + 1;
      }
      return {
        start,
        end,
        topPad: start * rowHeight,
        bottomPad: (total - end) * rowHeight,
      };
    }
    const w = computeWindow({
      offsets: offsets.value,
      scrollTop: scrollTop.value,
      containerHeight: containerHeight.value,
      overscan: VIRTUAL_OVERSCAN,
      dragged: params.draggedRow.value,
    });
    if (!hasUnmeasured.value) return w;
    return { ...w, bottomPad: w.bottomPad + estimate.value };
  });

  const visibleRows = computed(() => {
    const { start, end } = virtualWindow.value;
    const result: { row: T; rowIdx: number }[] = [];
    for (let i = start; i < end; i++) {
      result.push({ row: params.data.value[i], rowIdx: i });
    }
    return result;
  });

  // Loading a sparse window is often expensive (network, IndexedDB, or both).
  // Scroll and ResizeObserver changes can arrive in the same frame, so publish
  // only the final window once the browser is about to paint it.
  let visibleRangeFrame: number | null = null;
  let pendingVisibleRange: { startIndex: number; endIndex: number } | null = null;
  if (params.onVisibleRangeChange) {
    watch(
      () => [virtualWindow.value.start, virtualWindow.value.end] as const,
      ([startIndex, endIndex]) => {
        // Without rAF (SSR, or a test environment that was torn down while
        // this post-flush watcher was still queued) there is no frame to
        // coalesce into: publish the window at once instead of throwing.
        if (typeof requestAnimationFrame !== 'function') {
          pendingVisibleRange = null;
          params.onVisibleRangeChange?.({ startIndex, endIndex });
          return;
        }
        pendingVisibleRange = { startIndex, endIndex };
        if (visibleRangeFrame !== null) return;
        let didRunSynchronously = false;
        const frame = requestAnimationFrame(() => {
          didRunSynchronously = true;
          visibleRangeFrame = null;
          const range = pendingVisibleRange;
          pendingVisibleRange = null;
          if (range) params.onVisibleRangeChange?.(range);
        });
        // Browsers schedule rAF asynchronously. Keeping this branch makes the
        // coalescer correct under test runners (and native shims) that invoke
        // rAF callbacks synchronously.
        if (!didRunSynchronously) visibleRangeFrame = frame;
      },
      { immediate: true, flush: 'post' }
    );
  }

  /** Buffered heights while measuring is suspended (column resize drag). */
  const pendingMeasurements = new Map<string, number>();

  function applyMeasurement(rowId: string, height: number): void {
    if (isUniform.value) return;
    if (height <= 0) return;
    const row = params.data.value.find((r) => params.rowIdKey(r) === rowId);
    if (row && params.isPlaceholderRow?.(row)) return;
    // Sub-pixel jitter from getBoundingClientRect must not re-trigger the
    // offset/window recomputation (each trigger re-renders the table).
    const prev = measured.value.get(rowId);
    if (prev !== undefined && Math.abs(prev - height) < 0.5) return;
    if (params.suspendMeasure?.value) {
      pendingMeasurements.set(rowId, height);
      return;
    }
    // Anchor: keep the first visible row visually fixed when above-window
    // heights change (avoids a jump when scrolling up into unmeasured rows).
    const anchorIdx = virtualWindow.value.start;
    const anchorRow = params.data.value[anchorIdx];
    const anchorId = anchorRow ? params.rowIdKey(anchorRow) : null;
    const beforeOffset = anchorId ? offsetOf(anchorIdx) : 0;

    measured.value.set(rowId, height);
    triggerRef(measured);

    if (anchorId) {
      const afterOffset = offsetOf(anchorIdx);
      const delta = afterOffset - beforeOffset;
      const el = params.tableRef.value;
      if (delta !== 0 && el) {
        el.scrollTop += delta;
        scrollTop.value = el.scrollTop;
      }
    }
    // Only when a *already measured* row grows (lazy-edit). First paint of
    // the bottom-most visible line would otherwise look like overflow and
    // walk scrollTop to the document end — table opened at the bottom.
    if (prev !== undefined && height > prev + 0.5) {
      keepRowBottomInView(rowId, height);
      // Sync scrollTop is clamped to the *pre-grow* scrollHeight (spacers
      // and the taller .tr have not committed yet). Retry after paint.
      scheduleKeepRowBottomInView(rowId);
    }
  }

  let keepBottomRaf: number | null = null;
  let keepBottomRowId: string | null = null;

  function scheduleKeepRowBottomInView(rowId: string): void {
    keepBottomRowId = rowId;
    if (keepBottomRaf != null) return;
    void nextTick(() => {
      keepBottomRaf = requestAnimationFrame(() => {
        keepBottomRaf = null;
        const id = keepBottomRowId;
        keepBottomRowId = null;
        if (!id) return;
        const h = measured.value.get(id);
        if (h != null) keepRowBottomInView(id, h);
      });
    });
  }

  function keepRowBottomInView(rowId: string, height: number): void {
    if (isUniform.value) return;
    const el = params.tableRef.value;
    if (!el) return;
    const idx = params.data.value.findIndex((r) => params.rowIdKey(r) === rowId);
    if (idx < 0) return;
    const headerH = el.querySelector('.thead')?.getBoundingClientRect().height ?? 0;
    const predictedBottom = headerH + offsetOf(idx) + leadingHeightOf(idx) + height;
    const bottomInset = params.bottomInset?.value ?? 0;
    const viewBottom = el.scrollTop + el.clientHeight - bottomInset;
    let overflow = predictedBottom - viewBottom;
    // After paint, the real box wins — sticky thead / borders / h-scrollbar
    // make the virtual prediction short, and the last line looks "half gone".
    const rowEl = observed.get(rowId);
    if (rowEl && typeof el.getBoundingClientRect === 'function') {
      const realOverflow =
        rowEl.getBoundingClientRect().bottom - el.getBoundingClientRect().bottom + bottomInset;
      if (Number.isFinite(realOverflow) && realOverflow > overflow) overflow = realOverflow;
    }
    if (overflow > 0.5) {
      el.scrollTop += overflow;
      scrollTop.value = el.scrollTop;
    }
  }

  function measureUniform(rowId: string, el: HTMLElement | null): void {
    if (el) {
      if (uniformEl && uniformEl !== el) return; // one reference row is enough
      uniformEl = el;
      uniformRowId = rowId;
      const h = el.getBoundingClientRect().height;
      if (h > 0 && h !== uniformPx.value) uniformPx.value = h;
      if (!uniformRo && typeof ResizeObserver !== 'undefined') {
        uniformRo = new ResizeObserver((entries) => {
          const height =
            entries[0]?.borderBoxSize?.[0]?.blockSize ??
            uniformEl?.getBoundingClientRect().height ??
            0;
          if (height > 0 && height !== uniformPx.value) uniformPx.value = height;
        });
        uniformRo.observe(el);
      }
    } else if (uniformEl && rowId === uniformRowId) {
      // Only the reference row's own unmount drops it (any unmount used to);
      // the next rendered row becomes the reference, the height cache stays.
      uniformRo?.disconnect();
      uniformRo = undefined;
      uniformEl = null;
      uniformRowId = null;
    }
  }

  function measureRow(rowId: string, el: HTMLElement | null): void {
    if (isUniform.value) {
      measureUniform(rowId, el);
      return;
    }
    if (el) {
      observed.set(rowId, el);
      rowIdByEl.set(el, rowId);
      ensureRowResizeObserver()?.observe(el);
      applyMeasurement(rowId, el.getBoundingClientRect().height);
    } else {
      const prev = observed.get(rowId);
      if (prev) {
        ro?.unobserve(prev);
        observed.delete(rowId);
      }
      // keep the cached height
    }
  }

  /**
   * Height of a leading row (group header). Same anchoring as a body row: the
   * row at the window start stays put when a header above it changes height.
   * Uniform tables measure these too — only their body rows are fixed.
   */
  function applyLeadingMeasurement(key: string, height: number): void {
    if (height <= 0) return;
    const prev = measuredLeading.value.get(key);
    if (prev !== undefined && Math.abs(prev - height) < 0.5) return;
    const anchorIdx = virtualWindow.value.start;
    const hasAnchor = anchorIdx < params.data.value.length;
    const beforeOffset = hasAnchor ? offsetOf(anchorIdx) : 0;

    measuredLeading.value.set(key, height);
    triggerRef(measuredLeading);

    if (!hasAnchor) return;
    const delta = offsetOf(anchorIdx) - beforeOffset;
    const el = params.tableRef.value;
    if (delta !== 0 && el) {
      el.scrollTop += delta;
      scrollTop.value = el.scrollTop;
    }
  }

  function measureLeading(key: string, el: HTMLElement | null): void {
    // An unmount keeps the cached height AND the observation: when a header
    // moves to another row, its replacement may already be mounted and must
    // not lose its observer to the old element's teardown. A header is only
    // ever replaced by the next element under the same key.
    if (!el) return;
    const prev = leadingObserved.get(key);
    if (prev !== el) {
      if (prev) leadingRo?.unobserve(prev);
      leadingObserved.set(key, el);
      leadingKeyByEl.set(el, key);
      ensureLeadingResizeObserver()?.observe(el);
    }
    applyLeadingMeasurement(key, el.getBoundingClientRect().height);
  }

  function ensureLeadingResizeObserver(): ResizeObserver | undefined {
    if (leadingRo || typeof ResizeObserver === 'undefined') return leadingRo;
    leadingRo = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const el = entry.target as HTMLElement;
        const key = leadingKeyByEl.get(el);
        if (key === undefined || leadingObserved.get(key) !== el) continue;
        applyLeadingMeasurement(
          key,
          entry.borderBoxSize?.[0]?.blockSize ?? el.getBoundingClientRect().height
        );
      }
    });
    return leadingRo;
  }

  // ResizeObserver: re-measure rows whose content height changes.
  // Created lazily so uniform rows never allocate a per-row observer.
  function ensureRowResizeObserver(): ResizeObserver | undefined {
    if (ro || typeof ResizeObserver === 'undefined') return ro;
    ro = new ResizeObserver((entries) => {
      if (isUniform.value) return;
      for (const entry of entries) {
        const el = entry.target as HTMLElement;
        const rowId = rowIdByEl.get(el);
        if (rowId === undefined || observed.get(rowId) !== el) continue;
        // borderBoxSize avoids the forced layout a getBoundingClientRect
        // inside the observer callback would cause.
        const height = entry.borderBoxSize?.[0]?.blockSize ?? el.getBoundingClientRect().height;
        applyMeasurement(rowId, height);
      }
    });
    return ro;
  }

  function stopRowMeasurement(): void {
    uniformRo?.disconnect();
    uniformRo = undefined;
    uniformEl = null;
    ro?.disconnect();
    ro = undefined;
    observed.clear();
    pendingMeasurements.clear();
    keepBottomRowId = null;
    if (keepBottomRaf != null) {
      cancelAnimationFrame(keepBottomRaf);
      keepBottomRaf = null;
    }
    if (measured.value.size > 0) {
      measured.value.clear();
      triggerRef(measured);
    }
  }

  // A runtime switch to fixed rows must release every variable-height resource;
  // switching back starts from the current seed and fresh visible-row measures.
  watch(
    isUniform,
    (uniform) => {
      if (uniform) stopRowMeasurement();
    },
    { flush: 'sync' }
  );

  // Flush buffered row heights once the suspension (column resize) ends.
  if (params.suspendMeasure) {
    watch(params.suspendMeasure, (suspended) => {
      if (suspended || pendingMeasurements.size === 0) return;
      const entries = [...pendingMeasurements.entries()];
      pendingMeasurements.clear();
      for (const [rowId, height] of entries) applyMeasurement(rowId, height);
    });
  }

  // Prune cache when row identities change. Windowed fills mutate in place
  // (same array ref, sparse:N → real id) so watching the array itself misses
  // the swap and leaves shimmer heights in the estimate.
  //
  // When rows disappear (delete), keep the first still-live visible row
  // visually put — otherwise the shorter prefix-sum leaves scrollTop sitting
  // too far down, then a later remasure / last-row grow snaps it back up.
  let prevRowIds: string[] = isUniform.value
    ? []
    : params.data.value.map((r) => params.rowIdKey(r));
  // Leading rows (group headers) above each of `prevRowIds`: the old offsets
  // must include them, with the heights known NOW (measured after the snapshot).
  const snapshotLeading = (count: number): (readonly string[])[] =>
    hasLeading.value ? Array.from({ length: count }, (_, i) => leadingKeysAt(i)) : [];
  let prevLeadingKeys = snapshotLeading(prevRowIds.length);
  watch(
    () => (isUniform.value ? null : params.data.value.map((r) => params.rowIdKey(r)).join('\0')),
    () => {
      if (isUniform.value) {
        prevRowIds = [];
        prevLeadingKeys = [];
        return;
      }
      const newIds = params.data.value.map((r) => params.rowIdKey(r));
      const oldIds = prevRowIds;
      const oldLeadingKeys = prevLeadingKeys;
      prevRowIds = newIds;
      prevLeadingKeys = snapshotLeading(newIds.length);

      const live = new Set(newIds);
      const el = params.tableRef.value;
      const est = estimate.value;
      const m = measured.value;

      let oldAnchorId: string | null = null;
      let oldAnchorOffset = 0;
      if (oldIds.length > 0 && el) {
        const oldOffsets = new Array<number>(oldIds.length + 1);
        oldOffsets[0] = 0;
        for (let i = 0; i < oldIds.length; i++) {
          oldOffsets[i + 1] =
            oldOffsets[i] +
            (m.get(oldIds[i]) ?? est) +
            leadingHeightOfKeys(oldLeadingKeys[i] ?? NO_LEADING) +
            trailingHeightOfId(oldIds[i]);
        }
        const st = el.scrollTop;
        let start = 0;
        for (let i = 0; i < oldIds.length; i++) {
          if (oldOffsets[i + 1] > st) {
            start = i;
            break;
          }
          start = i;
        }
        // The anchor is the row itself, below its own leading rows.
        const rowTop = (i: number) =>
          oldOffsets[i] + leadingHeightOfKeys(oldLeadingKeys[i] ?? NO_LEADING);
        for (let i = start; i < oldIds.length; i++) {
          if (live.has(oldIds[i])) {
            oldAnchorId = oldIds[i];
            oldAnchorOffset = rowTop(i);
            break;
          }
        }
        if (oldAnchorId === null) {
          for (let i = start - 1; i >= 0; i--) {
            if (live.has(oldIds[i])) {
              oldAnchorId = oldIds[i];
              oldAnchorOffset = rowTop(i);
              break;
            }
          }
        }
      }

      let changed = false;
      for (const id of [...m.keys()]) {
        if (!live.has(id)) {
          m.delete(id);
          changed = true;
        }
      }
      if (changed) triggerRef(measured);

      if (oldAnchorId && el && live.has(oldAnchorId)) {
        const newIdx = newIds.indexOf(oldAnchorId);
        if (newIdx >= 0) {
          const delta = offsetOf(newIdx) + leadingHeightOf(newIdx) - oldAnchorOffset;
          if (Math.abs(delta) > 0.5) {
            el.scrollTop = Math.max(0, el.scrollTop + delta);
            scrollTop.value = el.scrollTop;
          }
        }
      }
    }
  );

  // scroll listener with rAF-throttle
  let scrollRaf: number | null = null;
  function onTableScroll() {
    if (scrollRaf !== null) return;
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = null;
      const el = params.tableRef.value;
      if (!el) return;
      if (el.scrollTop !== scrollTop.value) scrollTop.value = el.scrollTop;
    });
  }

  // observe if the table size changes to adapt window size and scroll position
  let tableResizeObserver: ResizeObserver | undefined;
  watch(params.tableRef, (el, oldEl) => {
    if (oldEl) {
      oldEl.removeEventListener('scroll', onTableScroll);
      tableResizeObserver?.unobserve(oldEl);
    }
    if (el) {
      containerHeight.value = el.clientHeight;
      el.addEventListener('scroll', onTableScroll, { passive: true });
      if (!tableResizeObserver) {
        tableResizeObserver = new ResizeObserver(() => {
          if (params.tableRef.value) containerHeight.value = params.tableRef.value.clientHeight;
        });
      }
      tableResizeObserver.observe(el);
      void nextTick();
    }
  });

  // cleanup
  onUnmounted(() => {
    if (scrollRaf !== null) {
      cancelAnimationFrame(scrollRaf);
      scrollRaf = null;
    }
    params.tableRef.value?.removeEventListener('scroll', onTableScroll);
    tableResizeObserver?.disconnect();
    if (visibleRangeFrame !== null) {
      cancelAnimationFrame(visibleRangeFrame);
      visibleRangeFrame = null;
    }
    stopRowMeasurement();
    leadingRo?.disconnect();
    leadingRo = undefined;
    leadingObserved.clear();
  });

  return {
    scrollTop,
    containerHeight,
    virtualWindow,
    visibleRows,
    estimate,
    offsetOf,
    totalHeight,
    measureRow,
    measureLeading,
  };
}
